import { AppSelectors } from 'src/app/store/app/app.selectors';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ModalController, RadioGroupCustomEvent } from '@ionic/angular';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Subscription } from 'rxjs';

import { GeneralHelper, MenuHelper, ScaleHelper } from 'src/app/helpers';
import {
  BaseMenuItem,
  DevicePlatform,
  PriceTypeWeightTypeMap,
  ScaleContainer,
  WeightUnit
} from 'src/app/models';
import { BaseProvider, TicketProvider } from 'src/app/providers';
import { ScaleProvider } from 'src/app/providers/scale.provider';
import { HardwareDeviceSelectors } from 'src/app/store/hardware-device/hardware-device.selector';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';

export type WeightedItemModalView =
  | 'setup'
  | 'weighting'
  | 'manual'
  | 'summary';

@Component({
  templateUrl: './weighted-item.modal.html',
  styleUrls: ['./weighted-item.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WeightedItemModal implements OnInit, OnDestroy {
  @ViewSelectSnapshot(AppSelectors.getSelectedLocaleCode)
  currentLang: string;

  @Input() menuItem: BaseMenuItem;
  @Input() parentUuid?: string;

  @ViewSelectSnapshot(LocationSelectors.getWeightContainers)
  readonly weightContainers: ScaleContainer[];
  @ViewSelectSnapshot(HardwareDeviceSelectors.platform)
  readonly platform: DevicePlatform;

  view: WeightedItemModalView = 'setup';
  selectedItem: ScaleContainer;
  scaleWeight: number;
  scaleWeightUnitType: WeightUnit;
  menuItemWeightUnitType: WeightUnit;
  scaleActivated = false;
  private scaleSubscription?: Subscription;
  private isDestroyed = false;
  readonly WEIGHT_UNIT = WeightUnit;
  readonly SCALE_WEIGHT_UNIT_OPTIONS: Record<
    WeightUnit,
    { value: WeightUnit; name: string }
  > = {
    [WeightUnit.Gram]: { value: WeightUnit.Gram, name: 'g' },
    [WeightUnit.Kilograms]: { value: WeightUnit.Kilograms, name: 'kg' },
    [WeightUnit.Pound]: { value: WeightUnit.Pound, name: 'lb' },
    [WeightUnit.Ounce]: { value: WeightUnit.Ounce, name: 'oz' }
  };

  constructor(
    private readonly modalController: ModalController,
    private readonly ticketProvider: TicketProvider,
    private readonly baseProvider: BaseProvider,
    private readonly scaleProvider: ScaleProvider,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly store: Store
  ) {}

  async ngOnInit() {
    console.log('ddevbug scale init: ', this.platform);
    if (!this.menuItem.scale) {
      MenuHelper.addPriceTo(this.menuItem);
    }

    if (this.platform === 'android') {
      try {
        const scaleSubscription = await this.scaleProvider.startListener(
          (info: any) => {
            this.scaleActivated = true;
            if (this.view === 'setup' || this.view === 'weighting') {
              this.handleScaleWeight(info.new_weight);
            }

            if (!this.weightContainers?.length && this.view === 'setup') {
              this.handleNextViewAfterContainer();
            }

            this.changeDetectorRef.markForCheck();
          }
        );

        // the modal may have been closed while the scale was starting
        if (this.isDestroyed) {
          scaleSubscription.unsubscribe();
        } else {
          this.scaleSubscription = scaleSubscription;
        }
      } catch (error) {
        this.scaleActivated = false;
        if (!this.weightContainers?.length && this.view === 'setup') {
          this.handleNextViewAfterContainer();
        }

        this.changeDetectorRef.markForCheck();
      }
    }

    this.menuItemWeightUnitType =
      this.menuItem.price_type &&
      PriceTypeWeightTypeMap[this.menuItem.price_type];
    this.scaleWeightUnitType = this.menuItemWeightUnitType;
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.scaleActivated = false;
    this.scaleSubscription?.unsubscribe();
  }

  handleScaleWeight(weight: string) {
    const rawWeight = ScaleHelper.convertRawWeight(weight);
    console.log('ddevbug scale 4: ', rawWeight, weight);
    if (rawWeight) {
      console.log('ddevbug scale 5: ', rawWeight, weight);
      this.scaleWeightUnitType = rawWeight.unit;
      this.scaleWeight = GeneralHelper.asDecimal(rawWeight.weight);
    }
  }

  onScaleWeightUnitTypeChange(e: Event) {
    const unitType = (e as RadioGroupCustomEvent<WeightUnit>).detail.value;
    const newWeight = ScaleHelper.weightConversion(
      this.scaleWeight,
      this.scaleWeightUnitType,
      unitType
    );
    this.scaleWeightUnitType = unitType;
    this.scaleWeight = GeneralHelper.asDecimal(newWeight);
  }

  back() {
    if (this.view === 'summary') {
      this.handleNextViewAfterContainer();
    } else if (this.view === 'weighting') {
      this.view = 'setup';
      this.selectedItem = null;
    } else if (this.view === 'manual') {
      if (this.scaleActivated) {
        this.view = 'weighting';
      } else {
        this.view = 'setup';
      }
    }
  }

  private handleNextViewAfterContainer() {
    this.view = this.scaleActivated ? 'weighting' : 'manual';
  }

  onSelectContainer(item: ScaleContainer) {
    this.handleNextViewAfterContainer();
    this.selectedItem = item;
  }

  onSkipContainer() {
    this.view = 'weighting';
    this.selectedItem = undefined;
  }

  onLockWeight() {
    this.view = 'summary';
    this.menuItem.scale.weight = GeneralHelper.asDecimal(
      ScaleHelper.weightConversion(
        this.scaleWeight,
        this.scaleWeightUnitType,
        this.menuItemWeightUnitType
      )
    );
    this.menuItem.scale.weight_unit = ScaleHelper.PriceTypeToWeightType(
      this.menuItem.price_type
    );

    if (this.selectedItem) {
      const containerWeight = ScaleHelper.weightConversion(
        this.selectedItem.weight,
        this.selectedItem.unit,
        this.menuItemWeightUnitType
      );
      this.menuItem.scale.weight_without_container = GeneralHelper.asDecimal(
        this.menuItem.scale.weight - containerWeight
      );
    } else {
      this.menuItem.scale.weight_without_container = this.menuItem.scale.weight;
    }

    this.menuItem.scale.item_price_by_weight =
      this.menuItem.scale.weight_without_container * this.menuItem.price > 0
        ? GeneralHelper.asDecimal(
            this.menuItem.scale.weight_without_container * this.menuItem.price
          )
        : 0;
    this.menuItem.amount = this.menuItem.scale.item_price_by_weight;
  }

  //Reset all values
  onStartOver() {
    if (!this.weightContainers?.length) {
      this.view = 'weighting';
    } else {
      this.view = 'setup';
    }
    this.scaleWeight = null;
    this.selectedItem = null;
  }

  onAddToOrder() {
    if (
      this.menuItem.scale &&
      this.menuItem.scale.weight_without_container > 0
    ) {
      const selectedNormalizedTicket = this.store.selectSnapshot(
        TicketsStateSelectors.normalizedTicket
      );

      this.ticketProvider.ticketItemAddingHandler({
        menuItem: this.menuItem,
        ticket_item_uuid: this.parentUuid,
        ticket_uuid: selectedNormalizedTicket?.device_ticket_uuid
      });
      this.onCloseModal();
    } else {
      this.baseProvider.popupAlert(
        this.translocoService.translate('common.warning'),
        this.translocoService.translate('errors.pleaseEnterValidWeight')
      );
    }
  }

  onCloseModal() {
    this.modalController.dismiss();
  }

  onManualWeight() {
    this.view = 'manual';
  }
}
